// src/pages/tasks/EditTask.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  OutlinedInput,
  Chip,
  CircularProgress,
  Avatar,
  Snackbar,
  Alert,
  Typography,
  Autocomplete,
  Paper,
  Divider,
  Stack,
  Tooltip,
  FormHelperText,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AvatarGroup,
  Checkbox,
  LinearProgress,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios, { CancelTokenSource } from "axios";
import dayjs, { Dayjs } from "dayjs";
import { useTheme } from "@mui/material/styles";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DescriptionIcon from "@mui/icons-material/Description";

import { TaskStatus } from "../../enums/task-status.enum";
import { TaskImportance } from "../../enums/task-importance.enum";
import { TaskRemindIn } from "../../enums/task_remind_in.enum";
import { baseUrl } from "../../constants";
import { useNavigate, useParams } from "react-router-dom";
import { getUserInfoFromToken } from "../../utils/Main";

type ID = number;
type TaskVisibility = "PRIVATE" | "PROJECT" | "DEPARTMENT" | "WORKSPACE";

interface TaskFormValues {
  title: string;
  description: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  status: TaskStatus;
  importance: TaskImportance;
  assignedUsers: ID[];
  visibility: TaskVisibility;
  remindIn: TaskRemindIn | null;
  remindAt: Dayjs | null;
  accountId: ID | null;
}

interface Department {
  id: ID;
  name: string;
}

interface Project {
  id: ID;
  name: string;
  departmentId?: ID;
}

interface User {
  id: ID;
  username?: string | null;
  imageUrl?: string | null;
  profile?: { imageUrl?: string | null; name?: string | null } | null;
}

const reminderOptions: Array<{ value: TaskRemindIn | null; label: string }> = [
  { value: null, label: "No reminder" },
  { value: TaskRemindIn.ONE_HOUR, label: "Before 1 hour (deadline)" },
  { value: TaskRemindIn.ONE_DAY, label: "Before 1 day (deadline)" },
  { value: TaskRemindIn.ONE_WEEK, label: "Before 1 week (deadline)" },
  { value: TaskRemindIn.ONE_MONTH, label: "Before 1 month (deadline)" },
  { value: TaskRemindIn.CUSTOM, label: "Custom date & time…" },
];

const nextBusinessMonday = () => {
  let d = dayjs().startOf("day");
  const dow = d.day();
  const add = (8 - dow) % 7 || 7;
  return d.add(add, "day").hour(9).minute(0);
};

const EditTask: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { taskId } = useParams<{ taskId: string }>();

  const token = localStorage.getItem("accessToken");
  const userInfo = getUserInfoFromToken();
  const workspaceId = userInfo?.workspaceId;

  // Meta for header display
  const [taskMeta, setTaskMeta] = useState<{
    id: number;
    title: string;
    type?: string | null;
    ref?: string | null;
  } | null>(null);

  // Loading flags
  const [loading, setLoading] = useState(false);
  const [fetchingTask, setFetchingTask] = useState(true);
  const [fetchingDepartments, setFetchingDepartments] = useState(true);
  const [fetchingProjects, setFetchingProjects] = useState(false);
  const [fetchingUsers, setFetchingUsers] = useState(false);
  const [fetchingAccounts, setFetchingAccounts] = useState(true);

  // Data stores
  const [myDepartments, setMyDepartments] = useState<Department[]>([]);
  const [userProjects, setUserProjects] = useState<Project[]>([]);
  const [deptProjectsMap, setDeptProjectsMap] = useState<Record<ID, Project[]>>(
    {}
  );
  const [usersByProject, setUsersByProject] = useState<Record<ID, User[]>>({});
  const [accounts, setAccounts] = useState<
    {
      id: ID;
      name: string;
      slug: string;
      email?: string | null;
      domain?: string | null;
    }[]
  >([]);

  // Selections
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<ID[]>([]);
  const [selectedProjectIds, setSelectedProjectIds] = useState<ID[]>([]);

  // User descriptions for assigned users
  const [userDescriptions, setUserDescriptions] = useState<Record<number, string>>({});

  // Cancel tokens
  const taskCancelRef = useRef<CancelTokenSource | null>(null);
  const deptsCancelRef = useRef<CancelTokenSource | null>(null);
  const userProjectsCancelRef = useRef<CancelTokenSource | null>(null);
  const deptProjectsCancelRefs = useRef<Record<ID, CancelTokenSource>>({});
  const projUsersCancelRefs = useRef<Record<ID, CancelTokenSource>>({});
  const accountsCancelRef = useRef<CancelTokenSource | null>(null);

  // Form
  const {
    handleSubmit,
    control,
    setValue,
    watch,
    trigger,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<TaskFormValues>({
    defaultValues: {
      title: "",
      description: "",
      startDate: dayjs(),
      endDate: dayjs().add(1, "hour"),
      status: TaskStatus.TODO,
      importance: TaskImportance.IMPORTANT,
      assignedUsers: [],
      visibility: "PRIVATE",
      remindIn: null,
      remindAt: null,
      accountId: null,
    },
    mode: "onChange",
  });

  const selectedRemindIn = watch("remindIn");
  const startDate = watch("startDate");
  const endDate = watch("endDate");
  const desc = watch("description") || "";
  const wordCount = desc.trim() ? desc.trim().split(/\s+/).length : 0;

  const busy =
    fetchingTask ||
    fetchingUsers ||
    fetchingProjects ||
    fetchingDepartments ||
    fetchingAccounts ||
    loading ||
    isSubmitting;

  // Pretty type label for header
  const typeLabel = useMemo(() => {
    if (!taskMeta?.type) return "Task";
    return taskMeta.type
      .toString()
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  }, [taskMeta?.type]);

  const presetReminderPreview = useMemo(() => {
    if (
      !endDate ||
      !selectedRemindIn ||
      selectedRemindIn === TaskRemindIn.CUSTOM
    )
      return "";
    const map: Record<TaskRemindIn, dayjs.ManipulateType | null> = {
      [TaskRemindIn.ONE_HOUR]: "hour",
      [TaskRemindIn.ONE_DAY]: "day",
      [TaskRemindIn.ONE_WEEK]: "week",
      [TaskRemindIn.ONE_MONTH]: "month",
      [TaskRemindIn.CUSTOM]: null,
    };
    const unit = map[selectedRemindIn];
    if (!unit) return "";
    const when = endDate.subtract(1, unit);
    return `Will fire on ${when.format("YYYY-MM-DD HH:mm")}`;
  }, [endDate, selectedRemindIn]);

  const onAxiosError = (err: unknown, friendly = "Something went wrong.") => {
    const message =
      (axios.isAxiosError(err) &&
        (err.response?.data?.message || err.message)) ||
      friendly;
    setSnackbar({ open: true, message, severity: "error" });
    console.error(err);
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  const [confirmOpen, setConfirmOpen] = useState(false);

  // ---------- FETCHERS ----------
  const fetchMyDepartments = useCallback(async () => {
    if (!token) return;
    deptsCancelRef.current?.cancel("edit departments load");
    deptsCancelRef.current = axios.CancelToken.source();
    setFetchingDepartments(true);
    try {
      const res = await axios.get(`${baseUrl}/departments/workspace/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope: "own" },
        cancelToken: deptsCancelRef.current.token,
      });
      const data = res.data;
      const list: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.departments)
        ? data.departments
        : [];
      const mapped: Department[] = list.map((d: any) => ({
        id: Number(d.id),
        name: d.name || d.title || d.departmentName || `Department #${d.id}`,
      }));
      mapped.sort((a, b) => a.name.localeCompare(b.name));
      setMyDepartments(mapped);
    } catch (e) {
      if (!axios.isCancel(e))
        onAxiosError(e, "Failed to load your departments.");
      setMyDepartments([]);
    } finally {
      setFetchingDepartments(false);
    }
  }, [token]);

  const fetchUserProjects = useCallback(async () => {
    if (!token) return;
    userProjectsCancelRef.current?.cancel("edit user projects load");
    userProjectsCancelRef.current = axios.CancelToken.source();
    try {
      const res = await axios.get(`${baseUrl}/projects/user/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { scope: "own" },
        cancelToken: userProjectsCancelRef.current.token,
      });
      const arr = Array.isArray(res.data) ? res.data : [];
      const mapped: Project[] = arr.map((p: any) => ({
        id: Number(p.id),
        name: p.name || p.title || `Project #${p.id}`,
        departmentId:
          typeof p.departmentId === "number" ? p.departmentId : undefined,
      }));
      setUserProjects(mapped);
    } catch (e) {
      if (!axios.isCancel(e)) onAxiosError(e, "Failed to load your projects.");
      setUserProjects([]);
    }
  }, [token]);

  const fetchProjectsForDepartment = useCallback(
    async (deptId: ID) => {
      if (!token) return;
      if (deptProjectsCancelRefs.current[deptId]) {
        deptProjectsCancelRefs.current[deptId].cancel("edit dept project load");
      }
      const src = axios.CancelToken.source();
      deptProjectsCancelRefs.current[deptId] = src;
      try {
        const res = await axios.get(
          `${baseUrl}/departments/${deptId}/projects`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cancelToken: src.token,
          }
        );
        const data = res.data;
        const list: any[] = Array.isArray(data)
          ? data
          : Array.isArray(data?.projects)
          ? data.projects
          : [];
        const raw: Project[] = list.map((p: any) => ({
          id: Number(p.id),
          name: p.name || p.title || `Project #${p.id}`,
          departmentId: deptId,
        }));

        const userProjIds = new Set(userProjects.map((p) => p.id));
        const filtered = raw.filter((p) => userProjIds.has(p.id));

        setDeptProjectsMap((prev) => ({
          ...prev,
          [deptId]: filtered.sort((a, b) => a.name.localeCompare(b.name)),
        }));
      } catch (e) {
        if (!axios.isCancel(e))
          onAxiosError(e, "Failed to load department projects.");
        setDeptProjectsMap((prev) => ({ ...prev, [deptId]: [] }));
      }
    },
    [token, userProjects]
  );

  const fetchUsersForProject = useCallback(
    async (projectId: ID) => {
      if (!token) return;
      if (projUsersCancelRefs.current[projectId]) {
        projUsersCancelRefs.current[projectId].cancel(
          "edit project users load"
        );
      }
      const src = axios.CancelToken.source();
      projUsersCancelRefs.current[projectId] = src;
      setFetchingUsers(true);
      try {
        const res = await axios.get(
          `${baseUrl}/projects/project-users/${projectId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
            cancelToken: src.token,
          }
        );
        const arr: any[] = Array.isArray(res.data) ? res.data : [];
        const mapped: User[] = arr
          .map((u: any) => ({
            id: Number(u.id ?? u.userId ?? u?.user?.id),
            username: u.username ?? u?.user?.username ?? null,
            imageUrl:
              u.imageUrl ??
              u?.profile?.imageUrl ??
              u?.user?.profile?.imageUrl ??
              null,
            profile: u.profile ?? u?.user?.profile ?? null,
          }))
          .filter((u) => !!u.id);
        setUsersByProject((prev) => ({ ...prev, [projectId]: mapped }));
      } catch (e) {
        if (!axios.isCancel(e))
          onAxiosError(e, "Failed to load project users.");
        setUsersByProject((prev) => ({ ...prev, [projectId]: [] }));
      } finally {
        setFetchingUsers(false);
      }
    },
    [token]
  );

  const fetchAccounts = useCallback(async () => {
    if (!token || !workspaceId) {
      setAccounts([]);
      setFetchingAccounts(false);
      return;
    }
    accountsCancelRef.current?.cancel("Edit fetchAccounts");
    accountsCancelRef.current = axios.CancelToken.source();
    setFetchingAccounts(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      let res;
      try {
        res = await axios.get(`${baseUrl}/accounts/visible`, {
          headers,
          cancelToken: accountsCancelRef.current.token,
        });
      } catch (e: any) {
        if (axios.isAxiosError(e) && e.response?.status === 404) {
          res = await axios.get(`${baseUrl}/accounts/${workspaceId}`, {
            headers,
            cancelToken: accountsCancelRef.current.token,
          });
        } else {
          throw e;
        }
      }
      const list = Array.isArray(res.data) ? res.data : [];
      setAccounts(
        list
          .map((a: any) => ({
            id: Number(a.id),
            name: a.name,
            slug: a.slug,
            email: a.email ?? null,
            domain: a.domain ?? null,
          }))
          .sort((a, b) => a.name.localeCompare(b.name))
      );
    } catch (e) {
      if (!axios.isCancel(e)) onAxiosError(e, "Failed to load accounts.");
      setAccounts([]);
    } finally {
      setFetchingAccounts(false);
    }
  }, [token, workspaceId]);

  // Load the task, prime selections & form
  const fetchTaskDetails = useCallback(
    async (idParam: string) => {
      if (!token) return;
      taskCancelRef.current?.cancel("new fetchTaskDetails");
      taskCancelRef.current = axios.CancelToken.source();
      setFetchingTask(true);
      try {
        const res = await axios.get(`${baseUrl}/tasks/${idParam}`, {
          headers: { Authorization: `Bearer ${token}` },
          cancelToken: taskCancelRef.current.token,
        });
        const task = res.data;

        // Header meta
        setTaskMeta({
          id: Number(task.id),
          title: task.title ?? "",
          type: task.type ?? null,
          ref: task.ref ?? null,
        });

        // Ensure we have user's projects
        if (userProjects.length === 0) {
          await fetchUserProjects();
        }

        // Selected primary project - handle both nested and flat structures
        const primaryProjectId = Number(
          task.projectId || 
          (task.project && typeof task.project.id === "number" ? task.project.id : null) ||
          null
        );
        setSelectedProjectIds(primaryProjectId ? [primaryProjectId] : []);

        // Infer department from project
        const p = userProjects.find((x) => x.id === primaryProjectId);
        if (p?.departmentId) {
          setSelectedDepartmentIds([p.departmentId]);
          await fetchProjectsForDepartment(p.departmentId);
        }

        // Load users for project
        if (primaryProjectId) {
          await fetchUsersForProject(primaryProjectId);
        }

        // Normalize account
        const normalizedAccountId =
          typeof task.accountId === "number"
            ? task.accountId
            : task.account && typeof task.account.id === "number"
            ? task.account.id
            : null;

        // Extract assigned user IDs - handle both nested (user.user.id) and flat (user.id) structures
        const assignedUserIds: number[] = [];
        if (Array.isArray(task.assignedUsers)) {
          task.assignedUsers.forEach((au: any) => {
            // Handle nested structure: { user: { id: ... } }
            const userId = au?.user?.id || au?.id;
            if (userId) {
              assignedUserIds.push(Number(userId));
            }
          });
        }

        // Fetch existing userDescriptions from UserTask records
        let existingUserDescriptions: Record<number, string> = {};
        try {
          const { data: userTaskData } = await axios.get(
            `${baseUrl}/tasks/usertasks-raw/${idParam}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              cancelToken: taskCancelRef.current.token,
            }
          );
          const rawUserTasks = Array.isArray(userTaskData) ? userTaskData : [];
          rawUserTasks.forEach((ut: any) => {
            const userId = ut?.userId || ut?.user?.id;
            if (userId && ut?.userDescription) {
              existingUserDescriptions[Number(userId)] = ut.userDescription;
            }
          });
        } catch (e) {
          // Silently fall back - userDescriptions will be empty
          console.warn("Failed to load userDescriptions:", e);
        }

        setUserDescriptions(existingUserDescriptions);

        reset({
          title: task.title ?? "",
          description: task.description ?? "",
          startDate: task.startDate ? dayjs(task.startDate) : dayjs(),
          endDate: task.endDate ? dayjs(task.endDate) : dayjs().add(1, "hour"),
          status: task.status as TaskStatus,
          importance: task.importance as TaskImportance,
          assignedUsers: assignedUserIds,
          visibility: (task.visibility as TaskVisibility) ?? "PRIVATE",
          remindIn: (task.remindIn as TaskRemindIn) ?? null,
          remindAt:
            task.remindIn === TaskRemindIn.CUSTOM && task.remindAt
              ? dayjs(task.remindAt)
              : null,
          accountId: normalizedAccountId,
        });
      } catch (e) {
        if (!axios.isCancel(e)) onAxiosError(e, "Failed to load task.");
      } finally {
        setFetchingTask(false);
      }
    },
    [
      token,
      reset,
      userProjects.length,
      fetchUserProjects,
      fetchProjectsForDepartment,
      fetchUsersForProject,
    ]
  );

  // ---------- EFFECTS ----------
  useEffect(() => {
    fetchMyDepartments();
    fetchUserProjects();
    fetchAccounts();
    return () => {
      deptsCancelRef.current?.cancel();
      userProjectsCancelRef.current?.cancel();
      Object.values(deptProjectsCancelRefs.current).forEach((c) => c?.cancel());
      Object.values(projUsersCancelRefs.current).forEach((c) => c?.cancel());
      accountsCancelRef.current?.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (taskId) fetchTaskDetails(taskId);
  }, [taskId, fetchTaskDetails]);

  // When departments change → fetch their projects
  const prevDeptLenRef = useRef<number>(0);
  useEffect(() => {
    const hadDeptsBefore = prevDeptLenRef.current > 0;
    const hasDeptsNow = selectedDepartmentIds.length > 0;

    if (!hasDeptsNow) {
      if (hadDeptsBefore) {
        setSelectedProjectIds([]);
      }
      prevDeptLenRef.current = 0;
      return;
    }

    setFetchingProjects(true);
    Promise.all(
      selectedDepartmentIds.map((did) => fetchProjectsForDepartment(did))
    )
      .catch(() => {})
      .finally(() => setFetchingProjects(false));

    prevDeptLenRef.current = selectedDepartmentIds.length;
  }, [selectedDepartmentIds, fetchProjectsForDepartment]);

  // When projects change → fetch users per project
  useEffect(() => {
    if (!selectedProjectIds.length) {
      setUsersByProject({});
      setValue("assignedUsers", []);
      return;
    }
    setFetchingUsers(true);
    Promise.all(selectedProjectIds.map((pid) => fetchUsersForProject(pid)))
      .catch(() => {})
      .finally(() => setFetchingUsers(false));
  }, [selectedProjectIds, fetchUsersForProject, setValue]);

  // Unsaved changes guard
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // ---------- DERIVED ----------
  const availableProjects: Project[] = useMemo(() => {
    let base: Project[] = [];

    if (!selectedDepartmentIds.length) {
      base = [...userProjects];
    } else {
      const map = new Map<ID, Project>();
      selectedDepartmentIds.forEach((did) => {
        (deptProjectsMap[did] || []).forEach((p) => map.set(p.id, p));
      });
      base = Array.from(map.values());
    }

    // Keep current selection present
    selectedProjectIds.forEach((pid) => {
      if (!base.some((p) => p.id === pid)) {
        const fallback = userProjects.find((p) => p.id === pid);
        if (fallback) base.unshift(fallback);
        else
          base.unshift({
            id: pid,
            name: `Project #${pid}`,
            departmentId: undefined,
          });
      }
    });

    base.sort((a, b) => a.name.localeCompare(b.name));

    // Pin first selected to top
    if (selectedProjectIds.length) {
      const first = selectedProjectIds[0];
      const idx = base.findIndex((p) => p.id === first);
      if (idx > 0) {
        const [match] = base.splice(idx, 1);
        base.unshift(match);
      }
    }

    return base;
  }, [
    selectedDepartmentIds,
    deptProjectsMap,
    userProjects,
    selectedProjectIds,
  ]);

  const availableUsers: User[] = useMemo(() => {
    const map = new Map<ID, User>();
    selectedProjectIds.forEach((pid) => {
      (usersByProject[pid] || []).forEach((u) => map.set(u.id, u));
    });
    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      const nameA = (
        a.profile?.name ||
        a.username ||
        `User #${a.id}`
      ).toLowerCase();
      const nameB = (
        b.profile?.name ||
        b.username ||
        `User #${b.id}`
      ).toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return arr;
  }, [selectedProjectIds, usersByProject]);

  // ---------- VALIDATION ----------
  const validateTitle = (v: string) =>
    (v || "").trim().length >= 3 || "Title must be at least 3 characters.";

  const validateDates = useCallback((s: Dayjs | null, e: Dayjs | null) => {
    if (!s || !e) return "Start and end dates are required.";
    if (e.isBefore(s)) return "End date must be after start date.";
    return true;
  }, []);

  // ---------- SUBMIT ----------
  const onSubmit = async (data: TaskFormValues) => {
    const dateCheck = validateDates(data.startDate, data.endDate);
    if (dateCheck !== true) {
      setSnackbar({
        open: true,
        message: String(dateCheck),
        severity: "error",
      });
      return;
    }

    if (data.remindIn === TaskRemindIn.CUSTOM && !data.remindAt) {
      setSnackbar({
        open: true,
        message: "Please pick a reminder date & time for the custom option.",
        severity: "error",
      });
      return;
    }

    if (!selectedProjectIds.length) {
      setSnackbar({
        open: true,
        message: "Please select at least one project.",
        severity: "error",
      });
      return;
    }
    const primaryProjectId = selectedProjectIds[0];

    const payload: any = {
      title: data.title.trim(),
      description: data.description.trim(),
      status: data.status,
      importance: data.importance,
      assignedUsers: data.assignedUsers,
      userDescriptions: userDescriptions, // Include user descriptions
      startDate: data.startDate
        ? dayjs(data.startDate).toISOString()
        : undefined,
      endDate: data.endDate ? dayjs(data.endDate).toISOString() : undefined,
      projectId: primaryProjectId,
      visibility: data.visibility,
      remindIn: data.remindIn ?? null,
      accountId: typeof data.accountId === "number" ? data.accountId : null,
    };

    if (data.remindIn === TaskRemindIn.CUSTOM) {
      payload.remindAt = dayjs(data.remindAt!).toISOString();
    } else {
      payload.remindAt = null;
    }

    try {
      setLoading(true);
      await axios.patch(`${baseUrl}/tasks/${taskId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSnackbar({
        open: true,
        message: "Task updated successfully!",
        severity: "success",
      });
      setTimeout(() => navigate(-1), 600);
    } catch (e) {
      onAxiosError(e, "Failed to update task. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Keyboard shortcut: Cmd/Ctrl+S
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const key = (e.key || "").toLowerCase();
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      const savePressed =
        (isMac && e.metaKey && key === "s") ||
        (!isMac && e.ctrlKey && key === "s");

      if (!savePressed) return;

      e.preventDefault();

      if (isSubmitting) return;

      void trigger().then((ok) => {
        if (ok) {
          void handleSubmit(onSubmit)();
        }
      });
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleSubmit, trigger, onSubmit, isSubmitting]);

  // Helpers
  const userLabel = (u: User) =>
    u.profile?.name || u.username || `User #${u.id}`;
  const userAvatarSrc = (u: User) => u.imageUrl || u.profile?.imageUrl || null;
  const selectAllDepartments = () =>
    setSelectedDepartmentIds(myDepartments.map((d) => d.id));
  const selectAllProjects = () =>
    setSelectedProjectIds(availableProjects.map((p) => p.id));
  const selectAllUsers = () =>
    setValue(
      "assignedUsers",
      availableUsers.map((u) => u.id),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );

  const onCancelAttempt = () => {
    if (isDirty) setConfirmOpen(true);
    else navigate(-1);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        id="page-container"
      sx={{ p: 2 }}
      >
        {/* Sticky header (advanced design) */}
        <Paper
          elevation={0}
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            mb: 2,
            px: { xs: 1, md: 2 },
            py: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backdropFilter: "blur(8px)",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(22,22,28,0.7)"
                : "rgba(255,255,255,0.9)",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{ minWidth: 0 }}
            >
              <Chip
                label="Editing"
                color="primary"
                icon={<EditOutlinedIcon />}
                sx={{
                  px: 1,
                  fontWeight: 700,
                  "& .MuiChip-label": { px: 0.5 },
                }}
              />
              <Chip
                label={typeLabel}
                variant="outlined"
                color="primary"
                icon={<Inventory2OutlinedIcon />}
                sx={{
                  px: 1,
                  fontWeight: 700,
                  "& .MuiChip-label": { px: 0.5, maxWidth: 180 },
                }}
              />
              <Stack sx={{ minWidth: 0 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight={800}
                  noWrap
                  title={taskMeta?.title || ""}
                  sx={{ maxWidth: { xs: 180, sm: 360, md: 520 } }}
                >
                  {taskMeta?.title || "—"}
                </Typography>
                {!!taskMeta?.ref && (
                  <Typography variant="caption" color="text.secondary">
                    Ref: <b>{taskMeta.ref}</b>
                  </Typography>
                )}
              </Stack>
            </Stack>

            <Stack direction="row" spacing={1}>
              <Tooltip title="Save (Ctrl/Cmd + S)">
                <span>
                  <Button
                    type="submit"
                    form="edit-task-form"
                    variant="contained"
                    startIcon={<SaveOutlinedIcon />}
                    disableElevation
                    disabled={
                      loading ||
                      isSubmitting ||
                      !!Object.keys(errors).length ||
                      fetchingTask
                    }
                    sx={{ fontWeight: 700 }}
                  >
                    {loading || isSubmitting ? (
                      <CircularProgress size={18} />
                    ) : (
                      "Save"
                    )}
                  </Button>
                </span>
              </Tooltip>
              <Button
                variant="outlined"
                color="inherit"
                onClick={onCancelAttempt}
                startIcon={<CloseIcon />}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>

          {busy && <LinearProgress sx={{ mt: 1 }} />}
        </Paper>

        <Box
          component="form"
          id="edit-task-form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
            {/* Left column */}
            <Stack sx={{ flex: 1 }} spacing={3}>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Stack spacing={2.25}>
                  {/* Title */}
                  <Controller
                    name="title"
                    control={control}
                    rules={{
                      required: "Title is required.",
                      validate: validateTitle,
                    }}
                    render={({ field }) => (
                      <>
                        <TextField
                          {...field}
                          label="Title"
                          required
                          fullWidth
                          autoFocus
                          error={!!errors.title}
                          helperText={errors.title?.message}
                        />
                        {!watch("title") && (
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.7, mt: -0.5 }}
                          >
                            Tip: use a verb + object, e.g., “Refactor
                            notification service”.
                          </Typography>
                        )}
                      </>
                    )}
                  />

                  {/* Dates */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Controller
                      name="startDate"
                      control={control}
                      rules={{ required: "Start date is required." }}
                      render={({ field }) => (
                        <DateTimePicker
                          label="Start Date & Time"
                          value={field.value}
                          onChange={(val) =>
                            setValue("startDate", val, { shouldValidate: true })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.startDate,
                              helperText: errors.startDate?.message,
                            },
                          }}
                        />
                      )}
                    />
                    <Controller
                      name="endDate"
                      control={control}
                      rules={{
                        required: "End date is required.",
                        validate: () => validateDates(startDate, endDate),
                      }}
                      render={({ field }) => (
                        <DateTimePicker
                          label="End Date & Time"
                          value={field.value}
                          onChange={(val) =>
                            setValue("endDate", val, { shouldValidate: true })
                          }
                          minDateTime={startDate || undefined}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.endDate,
                              helperText:
                                errors.endDate?.message ||
                                (startDate &&
                                  endDate &&
                                  endDate.isBefore(startDate) &&
                                  "End date must be after start date."),
                            },
                          }}
                        />
                      )}
                    />
                  </Stack>

                  {/* Quick deadline presets */}
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setValue("endDate", dayjs().hour(17).minute(0), {
                          shouldValidate: true,
                        })
                      }
                    >
                      Today 5:00 PM
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setValue(
                          "endDate",
                          dayjs().add(1, "day").hour(10).minute(0),
                          { shouldValidate: true }
                        )
                      }
                    >
                      Tomorrow 10:00 AM
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        setValue("endDate", nextBusinessMonday(), {
                          shouldValidate: true,
                        })
                      }
                    >
                      Next Monday 9:00 AM
                    </Button>
                  </Box>

                  {/* Status & Importance */}
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Controller
                      name="status"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl fullWidth required error={!!errors.status}>
                          <InputLabel>Status</InputLabel>
                          <Select
                            {...field}
                            input={<OutlinedInput label="Status" />}
                          >
                            <MenuItem value={TaskStatus.TODO}>Todo</MenuItem>
                            <MenuItem value={TaskStatus.IN_PROGRESS}>
                              In Progress
                            </MenuItem>
                            <MenuItem value={TaskStatus.DONE}>Done</MenuItem>
                            <MenuItem value={TaskStatus.POSTPONED}>
                              Postponed
                            </MenuItem>
                          </Select>
                          {errors.status && (
                            <FormHelperText>
                              Task status is required.
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="importance"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          required
                          error={!!errors.importance}
                        >
                          <InputLabel>Importance</InputLabel>
                          <Select
                            {...field}
                            input={<OutlinedInput label="Importance" />}
                          >
                            <MenuItem value={TaskImportance.NOT_IMPORTANT}>
                              Not Important
                            </MenuItem>
                            <MenuItem value={TaskImportance.IMPORTANT}>
                              Important
                            </MenuItem>
                            <MenuItem value={TaskImportance.VERY_IMPORTANT}>
                              Very Important
                            </MenuItem>
                          </Select>
                          {errors.importance && (
                            <FormHelperText>
                              Importance is required.
                            </FormHelperText>
                          )}
                        </FormControl>
                      )}
                    />
                  </Stack>

                  {/* Visibility */}
                  <Controller
                    name="visibility"
                    control={control}
                    rules={{ required: "Visibility is required." }}
                    render={({ field }) => (
                      <FormControl
                        fullWidth
                        required
                        error={!!errors.visibility}
                      >
                        <InputLabel>Visibility</InputLabel>
                        <Select
                          {...field}
                          input={<OutlinedInput label="Visibility" />}
                        >
                          <MenuItem value={"PRIVATE"}>PRIVATE</MenuItem>
                          <MenuItem value={"PROJECT"}>PROJECT</MenuItem>
                          <MenuItem value={"DEPARTMENT"}>DEPARTMENT</MenuItem>
                          <MenuItem value={"WORKSPACE"}>WORKSPACE</MenuItem>
                        </Select>
                        <FormHelperText>
                          PRIVATE: only creator & assignees • PROJECT: members
                          of this task’s project • DEPARTMENT: members of the
                          project’s department • WORKSPACE: everyone in the
                          workspace.
                        </FormHelperText>
                      </FormControl>
                    )}
                  />

                  {/* Reminder */}
                  <Controller
                    name="remindIn"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Reminder</InputLabel>
                        <Select
                          {...field}
                          input={<OutlinedInput label="Reminder" />}
                          onChange={(e) => {
                            const nextVal = e.target
                              .value as TaskRemindIn | null;
                            field.onChange(nextVal);
                            if (nextVal !== TaskRemindIn.CUSTOM) {
                              setValue("remindAt", null, { shouldDirty: true });
                            }
                          }}
                        >
                          {reminderOptions.map((opt) => (
                            <MenuItem
                              key={String(opt.value)}
                              value={opt.value as any}
                            >
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          Presets fire <b>before the task's end date</b> by the
                          selected amount.
                        </FormHelperText>
                        {presetReminderPreview && (
                          <Box sx={{ mt: 1 }}>
                            <Chip size="small" label={presetReminderPreview} />
                          </Box>
                        )}
                      </FormControl>
                    )}
                  />

                  {selectedRemindIn === TaskRemindIn.CUSTOM && (
                    <Controller
                      name="remindAt"
                      control={control}
                      rules={{
                        required: "Pick a date & time for the custom reminder.",
                      }}
                      render={({ field }) => (
                        <DateTimePicker
                          label="Custom reminder at"
                          value={field.value}
                          onChange={(val) =>
                            setValue("remindAt", val, { shouldValidate: true })
                          }
                          minDateTime={dayjs()}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.remindAt,
                              helperText:
                                errors.remindAt?.message ||
                                "Pick an exact date & time",
                            },
                          }}
                        />
                      )}
                    />
                  )}

                  {/* Departments (optional) */}
                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 0.75 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">
                          Department(s)
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={selectedDepartmentIds.length}
                        />
                      </Stack>
                      <Button
                        size="small"
                        onClick={selectAllDepartments}
                        disabled={
                          fetchingDepartments || myDepartments.length === 0
                        }
                      >
                        Select all
                      </Button>
                    </Stack>
                    {fetchingDepartments ? (
                      <Skeleton variant="rounded" height={56} />
                    ) : (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={myDepartments}
                        filterSelectedOptions
                        value={myDepartments.filter((d) =>
                          selectedDepartmentIds.includes(d.id)
                        )}
                        onChange={(_, vals) =>
                          setSelectedDepartmentIds(vals.map((v) => v.id))
                        }
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        getOptionLabel={(opt) => opt.name}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.id}>
                            <Checkbox sx={{ mr: 1 }} checked={selected} />
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search departments (optional)"
                            placeholder="Type to search…"
                          />
                        )}
                      />
                    )}
                    <FormHelperText sx={{ ml: 1, mt: 0.5 }}>
                      Leave empty to choose from all your projects.
                    </FormHelperText>
                  </Box>

                  {/* Projects */}
                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 0.75 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">Project(s)</Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={selectedProjectIds.length}
                        />
                      </Stack>
                      <Button
                        size="small"
                        onClick={selectAllProjects}
                        disabled={
                          fetchingProjects || availableProjects.length === 0
                        }
                      >
                        Select all
                      </Button>
                    </Stack>
                    {fetchingProjects ? (
                      <Skeleton variant="rounded" height={56} />
                    ) : (
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        options={availableProjects}
                        filterSelectedOptions
                        value={availableProjects.filter((p) =>
                          selectedProjectIds.includes(p.id)
                        )}
                        onChange={(_, vals) =>
                          setSelectedProjectIds(vals.map((v) => v.id))
                        }
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        getOptionLabel={(opt) => opt.name}
                        renderOption={(props, option, { selected }) => (
                          <li {...props} key={option.id}>
                            <Checkbox sx={{ mr: 1 }} checked={selected} />
                            {option.name}
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Search projects"
                            placeholder="Type to search…"
                          />
                        )}
                      />
                    )}
                    <FormHelperText sx={{ ml: 1, mt: 0.5 }}>
                      Only your assigned projects are shown. The{" "}
                      <b>first selected project</b> will be saved as the task’s
                      primary project.
                    </FormHelperText>
                  </Box>

                  {/* Assigned Users */}
                  <Box>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 0.75 }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">
                          Assigned Users
                        </Typography>
                        <Chip
                          size="small"
                          variant="outlined"
                          label={watch("assignedUsers")?.length || 0}
                        />
                      </Stack>
                      <Button
                        size="small"
                        onClick={selectAllUsers}
                        disabled={fetchingUsers || availableUsers.length === 0}
                      >
                        Select all
                      </Button>
                    </Stack>

                    {fetchingUsers ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={20} />
                        <Typography variant="body2">Loading users…</Typography>
                      </Box>
                    ) : (
                      <>
                        {Array.isArray(watch("assignedUsers")) &&
                          watch("assignedUsers").length > 0 && (
                            <AvatarGroup
                              max={6}
                              sx={{ justifyContent: "flex-start", mb: 1 }}
                            >
                              {watch("assignedUsers").map((uid: number) => {
                                const u = availableUsers.find(
                                  (x) => x.id === uid
                                );
                                const src = u ? userAvatarSrc(u) : null;
                                const name = u ? userLabel(u) : `User #${uid}`;
                                return (
                                  <Avatar key={uid} src={src || ""} alt={name}>
                                    {name?.[0]}
                                  </Avatar>
                                );
                              })}
                            </AvatarGroup>
                          )}

                        <Controller
                          name="assignedUsers"
                          control={control}
                          rules={{
                            validate: (arr) =>
                              Array.isArray(arr) && arr.length > 8
                                ? "Please limit to 8 assignees."
                                : Array.isArray(arr) && arr.length > 0
                                ? true
                                : "Assign at least one user.",
                          }}
                          render={({ field }) => (
                            <Autocomplete
                              multiple
                              disableCloseOnSelect
                              options={availableUsers}
                              value={availableUsers.filter((u) =>
                                field.value?.includes(u.id)
                              )}
                              onChange={(_, vals) => {
                                const newIds = vals.map((v) => v.id);
                                field.onChange(newIds);
                                // Clean up descriptions for removed users
                                setUserDescriptions((prev) => {
                                  const updated = { ...prev };
                                  Object.keys(updated).forEach((key) => {
                                    if (!newIds.includes(Number(key))) {
                                      delete updated[Number(key)];
                                    }
                                  });
                                  return updated;
                                });
                              }}
                              isOptionEqualToValue={(o, v) => o.id === v.id}
                              getOptionLabel={(opt) => userLabel(opt)}
                              renderOption={(props, option, { selected }) => (
                                <li {...props} key={option.id}>
                                  <Checkbox sx={{ mr: 1 }} checked={selected} />
                                  <Avatar
                                    src={userAvatarSrc(option) || ""}
                                    sx={{ width: 24, height: 24, mr: 1.0 }}
                                  >
                                    {userLabel(option)[0]}
                                  </Avatar>
                                  {userLabel(option)}
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Search users"
                                  placeholder={
                                    availableUsers.length
                                      ? "Type a name…"
                                      : "Select project(s) first"
                                  }
                                  error={!!errors.assignedUsers}
                                  helperText={
                                    (errors.assignedUsers?.message as string) ||
                                    undefined
                                  }
                                />
                              )}
                            />
                          )}
                        />

                        {/* User Descriptions Section */}
                        {Array.isArray(watch("assignedUsers")) &&
                          watch("assignedUsers").length > 0 && (
                            <Box sx={{ mt: 3 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  mb: 1.5,
                                  fontWeight: 600,
                                  color: theme.palette.text.primary,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <DescriptionIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                                User Descriptions (Optional)
                              </Typography>
                              <Stack spacing={2}>
                                {watch("assignedUsers").map((uid: number) => {
                                  const u = availableUsers.find((x) => x.id === uid);
                                  const userName = u ? userLabel(u) : `User #${uid}`;
                                  const userImage = u ? userAvatarSrc(u) : null;
                                  
                                  return (
                                    <Paper
                                      key={uid}
                                      elevation={0}
                                      sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: theme.palette.background.paper,
                                        transition: "all 0.2s ease",
                                        "&:hover": {
                                          border: `1px solid ${theme.palette.primary.main}`,
                                          boxShadow: 1,
                                        },
                                      }}
                                    >
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                                        <Avatar
                                          src={userImage || ""}
                                          sx={{
                                            width: 32,
                                            height: 32,
                                            border: `2px solid ${theme.palette.primary.main}`,
                                          }}
                                        >
                                          {userName[0]}
                                        </Avatar>
                                        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                                          {userName}
                                        </Typography>
                                      </Box>
                                      <TextField
                                        fullWidth
                                        multiline
                                        rows={3}
                                        placeholder={`Add description or notes for ${userName}...`}
                                        value={userDescriptions[uid] || ""}
                                        onChange={(e) =>
                                          setUserDescriptions((prev) => ({
                                            ...prev,
                                            [uid]: e.target.value,
                                          }))
                                        }
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: 1.5,
                                            fontSize: "0.875rem",
                                          },
                                        }}
                                      />
                                    </Paper>
                                  );
                                })}
                              </Stack>
                            </Box>
                          )}
                      </>
                    )}
                  </Box>

                  {/* Account (optional) */}
                  <Controller
                    name="accountId"
                    control={control}
                    render={({ field }) => {
                      const selected =
                        accounts.find((a) => a.id === field.value) ?? null;
                      return (
                        <Box>
                          {fetchingAccounts ? (
                            <Skeleton variant="rounded" height={56} />
                          ) : (
                            <Autocomplete
                              value={selected}
                              onChange={(_, val) =>
                                field.onChange(val ? val.id : null)
                              }
                              options={accounts}
                              getOptionLabel={(opt) =>
                                opt
                                  ? `${opt.name}${
                                      opt.slug ? ` (${opt.slug})` : ""
                                    }`
                                  : ""
                              }
                              isOptionEqualToValue={(o, v) => o.id === v.id}
                              renderOption={(props, option) => (
                                <li {...props} key={option.id}>
                                  <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    sx={{ width: "100%" }}
                                  >
                                    <span>
                                      {option.name}
                                      {option.slug ? ` (${option.slug})` : ""}
                                    </span>
                                    <Typography
                                      variant="caption"
                                      sx={{ opacity: 0.7 }}
                                    >
                                      {option.domain || option.email || ""}
                                    </Typography>
                                  </Stack>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Account (optional)"
                                  placeholder="Search accounts…"
                                  fullWidth
                                />
                              )}
                            />
                          )}
                          <FormHelperText sx={{ ml: 1, mt: 0.5 }}>
                            Link this task to an account
                            (customer/vendor/partner) if applicable.
                          </FormHelperText>
                        </Box>
                      );
                    }}
                  />
                </Stack>
              </Paper>
            </Stack>

            {/* Right column */}
            <Stack sx={{ flex: 1 }} spacing={3}>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  height: "100%",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Description
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Controller
                  name="description"
                  control={control}
                  rules={{
                    required: "Description is required.",
                    validate: (v) =>
                      (v || "").trim().length >= 5 ||
                      "Please add a bit more detail.",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Describe the task update"
                      multiline
                      minRows={10}
                      fullWidth
                      required
                      error={!!errors.description}
                      helperText={
                        errors.description?.message ||
                        `${wordCount} words • aim for 10+ for clarity`
                      }
                    />
                  )}
                />
              </Paper>
            </Stack>
          </Stack>
        </Box>

        {/* Cancel confirmation dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Discard changes?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              You have unsaved edits. This action can’t be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Keep editing</Button>
            <Button
              color="error"
              variant="contained"
              onClick={() => navigate(-1)}
            >
              Discard
            </Button>
          </DialogActions>
        </Dialog>

        {/* Saving overlay */}
        {(loading || isSubmitting) && (
          <Box
            sx={{
              position: "fixed",
              inset: 0,
              bgcolor: "rgba(0,0,0,.12)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1300,
            }}
          >
            <Paper sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
              <CircularProgress size={20} />
              <Typography>Saving changes…</Typography>
            </Paper>
          </Box>
        )}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default EditTask;
